/**
 * The branded ending clip (#67).
 *
 * Every project's playback finishes on a short Life Movie Maker card. It is a
 * real fMP4 clip appended to the same MSE SourceBuffer as the user's footage,
 * so it plays gaplessly out of the last clip like any other.
 *
 * ── Why it is generated at runtime rather than shipped as a file ───────────
 *
 * MSE requires a uniform track structure across a SourceBuffer: a clip whose
 * init segment declares different tracks than the first clip's is skipped
 * outright (see the codec check in `useMSEPlayer`). User clips are encoded by
 * each user's OWN browser, so a single prebuilt outro encoded on a build
 * machine is exactly the artifact most likely to mismatch — and it could not be
 * auto-repaired, because repair looks the clip up in the database and a
 * synthetic clip has no row.
 *
 * Generating it here, through the same mediabunny pipeline and on the same
 * device that produced the user's minis, makes the tracks match by
 * construction. It also costs no bandwidth and no storage.
 *
 * ── Why it is not a database row ───────────────────────────────────────────
 *
 * The outro is not a timeline item: it has no date, no sort_order, no crop, no
 * uploader, no permissions. Giving it a row would push it through grouping,
 * filtering, drag-and-drop, duplicate detection, overlays and RLS, and then
 * require suppressing it in every one of them. Instead it exists ONLY inside
 * the MSE clip registry, which is why it never appears in the timeline.
 */

/** Sentinel clip id. Never a real UUID, so it can't collide with a video row. */
export const OUTRO_CLIP_ID = '__lmm_outro__';

/** Matches the 960x720 (4:3) every `_cropped_mini.mp4` is composited to. */
const OUTRO_WIDTH = 960;
const OUTRO_HEIGHT = 720;

const OUTRO_FPS = 30;
const OUTRO_BITRATE = 500_000; // Static card — compresses very well.
const OUTRO_AUDIO_BITRATE = 128_000; // Must match the minis' audio config.

/** Total clip length. 0.6s fading up, then the card holds. */
export const OUTRO_DURATION_SECONDS = 3.5;
const OUTRO_FADE_SECONDS = 0.6;

/** The app's brand gradient — the same stops as `branding/chosen.svg`. */
const BRAND_STOPS = ['#FB923C', '#EC4899', '#9333EA'] as const;

const LOGO_SRC = '/favicon.svg';
const WORDMARK = 'Life Movie Maker';

/**
 * Cached across the session: the outro is identical for every project, so it
 * is generated at most once per page load. `null` while not yet attempted.
 */
let outroPromise: Promise<Blob | null> | null = null;

/** Load the logo SVG at an explicit pixel size.
 *
 * SVGs without intrinsic dimensions rasterize inconsistently across browsers,
 * so the size is forced here rather than relying on `naturalWidth`.
 */
function loadLogo(size: number): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.width = size;
    img.height = size;
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // Card still works without the mark.
    img.src = LOGO_SRC;
  });
}

/** Draw the brand card. `progress` 0→1 fades it up from black. */
function drawOutroFrame(
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement | null,
  progress: number
) {
  // Fade from black rather than cross-fading from the user's last frame: a
  // true cross-fade would have to be rendered per project, which would mean
  // re-encoding the outro for every timeline instead of once per session.
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, OUTRO_WIDTH, OUTRO_HEIGHT);
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, progress));

  const gradient = ctx.createLinearGradient(0, 0, OUTRO_WIDTH, OUTRO_HEIGHT);
  gradient.addColorStop(0, BRAND_STOPS[0]);
  gradient.addColorStop(0.5, BRAND_STOPS[1]);
  gradient.addColorStop(1, BRAND_STOPS[2]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, OUTRO_WIDTH, OUTRO_HEIGHT);

  const logoSize = 200;
  const logoY = OUTRO_HEIGHT / 2 - 130;
  if (logo) {
    ctx.drawImage(logo, (OUTRO_WIDTH - logoSize) / 2, logoY, logoSize, logoSize);
  }

  // White text on the gradient, matching the app's design language.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 62px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  ctx.shadowColor = 'rgba(0,0,0,0.28)';
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 3;
  ctx.fillText(WORDMARK, OUTRO_WIDTH / 2, logoY + logoSize + 78);

  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.globalAlpha *= 0.85;
  ctx.font = '500 30px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  ctx.fillText('lifemoviemaker.com', OUTRO_WIDTH / 2, logoY + logoSize + 146);

  ctx.restore();
}

/**
 * Render the outro card to an fMP4 blob.
 *
 * Mirrors `generatePhotoVideoMini`'s encoder configuration exactly (avc video +
 * silent aac audio, fragmented MP4) so the result is track-compatible with the
 * user's clips.
 *
 * Returns null — rather than throwing — if this browser can't produce a
 * compatible clip. A missing outro is a non-event; a mismatched one would be
 * silently dropped by the SourceBuffer anyway.
 */
async function renderOutro(): Promise<Blob | null> {
  try {
    const {
      Output,
      BufferTarget,
      Mp4OutputFormat,
      CanvasSource,
      AudioBufferSource,
      canEncodeVideo,
      canEncodeAudio,
    } = await import('mediabunny');

    // Without BOTH tracks the init segment declares a different track set than
    // the user's clips and MSE would skip the clip. Better to have no outro.
    if (!(await canEncodeVideo('avc'))) return null;
    if (!(await canEncodeAudio('aac'))) return null;

    const canvas = document.createElement('canvas');
    canvas.width = OUTRO_WIDTH;
    canvas.height = OUTRO_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const logo = await loadLogo(200);

    const target = new BufferTarget();
    const output = new Output({
      format: new Mp4OutputFormat({ fastStart: 'fragmented' }),
      target,
    });

    const videoSource = new CanvasSource(canvas, {
      codec: 'avc',
      bitrate: OUTRO_BITRATE,
    });
    output.addVideoTrack(videoSource, { frameRate: OUTRO_FPS });

    const audioSource = new AudioBufferSource({
      codec: 'aac',
      bitrate: OUTRO_AUDIO_BITRATE,
    });
    output.addAudioTrack(audioSource);

    await output.start();

    const totalFrames = Math.ceil(OUTRO_FPS * OUTRO_DURATION_SECONDS);
    const fadeFrames = Math.max(1, Math.round(OUTRO_FPS * OUTRO_FADE_SECONDS));
    for (let i = 0; i < totalFrames; i++) {
      // Ease-out on the fade so it settles rather than stopping abruptly.
      const linear = Math.min(1, i / fadeFrames);
      drawOutroFrame(ctx, logo, 1 - Math.pow(1 - linear, 3));
      await videoSource.add(i / OUTRO_FPS, 1 / OUTRO_FPS);
    }

    // Silence, so the user's audio ends cleanly rather than running under the card.
    const sampleRate = 48000;
    const frames = Math.ceil(sampleRate * OUTRO_DURATION_SECONDS);
    const audioCtx = new OfflineAudioContext(1, frames, sampleRate);
    await audioSource.add(audioCtx.createBuffer(1, frames, sampleRate));

    await output.finalize();
    if (!target.buffer) return null;

    return new Blob([target.buffer], { type: 'video/mp4' });
  } catch (err) {
    console.error('[Outro] Generation failed:', err);
    return null;
  }
}

/**
 * The outro clip's bytes, generated once per session.
 *
 * Safe to call repeatedly and from several places at once — callers share one
 * in-flight promise. Resolves to null when this browser can't produce a
 * track-compatible clip, in which case playback simply ends on the last real
 * clip as before.
 */
export function getOutroClip(): Promise<Blob | null> {
  if (!outroPromise) {
    outroPromise = renderOutro();
  }
  return outroPromise;
}

/** Start generating in the background so the clip is ready before it's needed. */
export function warmOutroClip(): void {
  void getOutroClip();
}
